/**
 * PricingHeader - Pricing box card for payment page
 *
 * Shows trial pricing in a card format with:
 * - "7-day free trial" heading
 * - "then $X/month" in salmon/coral
 * - "*billed annually at $X" footnote with optional strikethrough for discounts
 * - Optional coupon badge with dismiss button
 */

interface PricingHeaderProps {
  /** Whether this is a trial offer */
  isTrial?: boolean;
  /** Annual price in dollars (regular, before discount) */
  annualPrice?: number;
  /** Whether pricing is loading */
  isLoading?: boolean;
  /** Applied coupon code (e.g. "HEALTH30") */
  couponCode?: string;
  /** Discounted annual price when coupon is applied */
  discountedAnnualPrice?: number;
  /** Callback when coupon badge dismiss is clicked */
  onRemoveCoupon?: () => void;
}

export function PricingHeader({
  isTrial = true,
  annualPrice,
  isLoading = false,
  couponCode,
  discountedAnnualPrice,
  onRemoveCoupon,
}: PricingHeaderProps) {
  const hasDiscount =
    discountedAnnualPrice !== undefined &&
    annualPrice !== undefined &&
    discountedAnnualPrice < annualPrice;

  const discountPercent =
    hasDiscount ? Math.round(((annualPrice - discountedAnnualPrice) / annualPrice) * 100) : 0;

  if (isLoading || annualPrice === undefined) {
    return (
      <div className="relative mt-3 w-full rounded-2xl border-2 border-slate-200 bg-white p-6">
        <div className="flex items-center justify-between">
          <div className="h-5 w-32 animate-pulse rounded bg-slate-200" />
          <div className="h-5 w-20 animate-pulse rounded bg-slate-200" />
        </div>
        <div className="mt-2 h-4 w-48 animate-pulse rounded bg-slate-200" />
      </div>
    );
  }

  if (isTrial) {
    return (
      <div className="relative mt-3 w-full rounded-2xl bg-white px-6 pt-5 pb-5 shadow-[0_4px_24px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.03)]">
        {/* Top row: trial label + price + $0 today */}
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-xl font-bold text-catch-dark-blue-90">
              7-day free trial
            </span>
            <p className="mt-1 text-base font-medium text-catch-dark-blue-90">
              then ${Math.round((hasDiscount ? discountedAnnualPrice : annualPrice) / 12)}/month
            </p>
            <p className="mt-0.5 text-sm text-gray-500">
              {hasDiscount ? (
                <>*billed annually at <span className="line-through">${annualPrice}</span> ${discountedAnnualPrice}</>
              ) : (
                <>*billed annually at ${annualPrice}</>
              )}
            </p>

            {/* Coupon badge + HSA/FSA badge */}
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {couponCode && hasDiscount && (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-green-300 bg-green-50 py-1 pr-2.5 pl-[7px] text-xs font-semibold text-green-600">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <circle cx="7" cy="7" r="6" fill="#22C55E" opacity="0.15" />
                    <path d="M4.5 7L6 8.5L9.5 5" stroke="#16A34A" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {couponCode.toUpperCase()}
                  <span className="text-green-400">·</span>
                  <span className="text-[11px] text-green-700">{discountPercent}% off</span>
                  {onRemoveCoupon && (
                    <button
                      type="button"
                      onClick={onRemoveCoupon}
                      className="ml-0.5 flex items-center"
                      aria-label="Remove coupon"
                    >
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M3 3L9 9M9 3L3 9" stroke="#86EFAC" strokeWidth="1.3" strokeLinecap="round" />
                      </svg>
                    </button>
                  )}
                </span>
              )}
              <span
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-catch-salmon"
                style={{ background: 'rgba(255, 148, 115, 0.08)', border: '1px solid rgba(255, 148, 115, 0.25)' }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M7 1L2.5 3V6.5C2.5 9.5 4.5 12 7 13C9.5 12 11.5 9.5 11.5 6.5V3L7 1Z" stroke="currentColor" strokeWidth="1.2" fill="currentColor" fillOpacity="0.12" />
                  <path d="M4.8 7.2L6.2 8.6L9.2 5.6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="text-xs font-semibold">HSA/FSA Eligible</span>
                <span className="-ml-px">&sup1;</span>
              </span>
            </div>
          </div>
          <div
            className="rounded-lg px-4 py-2 text-center"
            style={{ background: 'rgba(255, 148, 115, 0.08)', border: '1px solid rgba(255, 148, 115, 0.25)' }}
          >
            <div className="text-2xl font-bold text-catch-salmon">$0</div>
            <div className="text-[11px] font-medium text-catch-salmon">today</div>
          </div>
        </div>
      </div>
    );
  }

  // Non-trial pricing (direct purchase)
  const effectiveAnnualPrice = hasDiscount ? discountedAnnualPrice : annualPrice;
  const monthlyPrice = Math.round(effectiveAnnualPrice / 12);

  return (
    <div className="relative mt-3 w-full rounded-2xl bg-white px-6 pt-5 pb-5 shadow-[0_4px_24px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.03)]">
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <span className="text-xl font-bold text-catch-dark-blue-90">
            Catch Membership
          </span>
          <p className="mt-1 text-base font-medium text-catch-dark-blue-90">
            ${monthlyPrice}/month
          </p>
          <p className="mt-0.5 text-sm text-gray-500">
            {hasDiscount ? (
              <>*billed annually at <span className="line-through">${annualPrice}</span> ${discountedAnnualPrice}</>
            ) : (
              <>*billed annually at ${annualPrice}</>
            )}
          </p>

          {/* Coupon badge + HSA/FSA badge */}
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {couponCode && hasDiscount && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-green-300 bg-green-50 py-1 pr-2.5 pl-[7px] text-xs font-semibold text-green-600">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <circle cx="7" cy="7" r="6" fill="#22C55E" opacity="0.15" />
                  <path d="M4.5 7L6 8.5L9.5 5" stroke="#16A34A" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {couponCode.toUpperCase()}
                <span className="text-green-400">&middot;</span>
                <span className="text-[11px] text-green-700">{discountPercent}% off</span>
                {onRemoveCoupon && (
                  <button
                    type="button"
                    onClick={onRemoveCoupon}
                    className="ml-0.5 flex items-center"
                    aria-label="Remove coupon"
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M3 3L9 9M9 3L3 9" stroke="#86EFAC" strokeWidth="1.3" strokeLinecap="round" />
                    </svg>
                  </button>
                )}
              </span>
            )}
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-catch-salmon"
              style={{ background: 'rgba(255, 148, 115, 0.08)', border: '1px solid rgba(255, 148, 115, 0.25)' }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M7 1L2.5 3V6.5C2.5 9.5 4.5 12 7 13C9.5 12 11.5 9.5 11.5 6.5V3L7 1Z" stroke="currentColor" strokeWidth="1.2" fill="currentColor" fillOpacity="0.12" />
                <path d="M4.8 7.2L6.2 8.6L9.2 5.6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className="text-xs font-semibold">HSA/FSA Eligible</span>
              <span className="-ml-px">&sup1;</span>
            </span>
          </div>
        </div>
        <div
          className="rounded-lg px-4 py-2 text-center"
          style={{ background: 'rgba(255, 148, 115, 0.08)', border: '1px solid rgba(255, 148, 115, 0.25)' }}
        >
          <div className="text-2xl font-bold text-catch-salmon">${monthlyPrice}</div>
          <div className="text-[11px] font-medium text-catch-salmon">/mo</div>
        </div>
      </div>
    </div>
  );
}

export default PricingHeader;
